import React, { useEffect, useState } from 'react'
import Select from 'react-select'
import tabsConfig, { TabConfig } from './tabsConfig'
import BackOfficeAPIClient from "../../clients/BackofficeAPIClient"

interface OrigineOption
{
    label: string
    value: number
}

interface Origine
{
    IDOrigineAction: number
    Famille_Libelle: string
    SousFamille_Libelle: string
    Libelle: string
    Type_Description: string
}

interface OrigineDefaultResponse
{
    IDOrigineActionDefault: number
    IDOrigineAction: number | null
    Contexte: string
}

type TabsState = Record<string, Record<string, number | null>>
type SavingState = Record<string, boolean>

const initTabsState = (): TabsState =>
    tabsConfig.reduce<TabsState>((acc, tab) => {
        acc[tab.id] = tab.fields.reduce<Record<string, number | null>>((fieldAcc, field) => {
            fieldAcc[field.key] = null
            return fieldAcc
        }, {})
        return acc
    }, {})

const initSavingState = (): SavingState =>
    tabsConfig.reduce<SavingState>((acc, tab) => {
        acc[tab.id] = false
        return acc
    }, {})

export default function List() {
    const backofficeApiClient = new BackOfficeAPIClient()

    const [ origineOptions, setOrigineOptions ] = useState<OrigineOption[]>([])
    const [ tabsState, setTabsState ] = useState<TabsState>(initTabsState)
    const [ savingState, setSavingState ] = useState<SavingState>(initSavingState)
    const [ isFetching, setIsFetching ] = useState<boolean>(true)
    const [ activeTab, setActiveTab ] = useState<string>(tabsConfig[0].id ?? "")

    const fetchOrigines = async (): Promise<OrigineOption[]> => {
        try {
            const json = await backofficeApiClient.get<Origine[]>('/origines')

            return json.map(item => ({
                label: `${item.Famille_Libelle} / ${item.SousFamille_Libelle} / ${item.Libelle} (${item.Type_Description})`,
                value: item.IDOrigineAction,
            }))
        } catch (error) {
            console.error(error)
            return []
        }
    }

    const fetchTabsData = async (origines: OrigineOption[]): Promise<TabsState> => {
        try {
            const json = await backofficeApiClient.get<OrigineDefaultResponse[]>('/origineDefault')

            const state = initTabsState()
            json.forEach((item) => {
                for (const tab of tabsConfig) {
                    if (tab.fields.some(f => f.key === item.Contexte)) {
                        const found = origines.find(opt => opt.value === item.IDOrigineAction)
                        state[tab.id][item.Contexte] = found ? found.value : null
                        break
                    }
                }
            })
            return state
        } catch (error) {
            console.error(error)
            return initTabsState()
        }
    }

    const saveTabData = async (tab: TabConfig) => {
        setSavingState(prev => ({ ...prev, [tab.id]: true }))

        await backofficeApiClient.put('/origineDefault', tabsState[tab.id])

        setSavingState(prev => ({ ...prev, [tab.id]: false }))
    }

    const handleSelect = (tabId: string, fieldKey: string, value: number) => {
        setTabsState(prev => ({
            ...prev,
            [tabId]: { ...prev[tabId], [fieldKey]: value },
        }))
    }

    useEffect(() => {
        (async () => {
            const origines = await fetchOrigines()
            setOrigineOptions(origines)

            setTabsState(await fetchTabsData(origines))

            setIsFetching(false)
        })()
    }, [])

    return (
        <div className="container-fluid">
            <div className="col">
                <div className="d-flex flex-row align-items-center mb-4" style={{ gap: "10px" }}>
                    <a href="/origines" className="text-muted" style={{ fontSize: "1.2em" }}>
                        <i className="fa-solid fa-arrow-left"></i>
                    </a>
                    <h2>Origines par défaut</h2>
                </div>

                <ul className="nav nav-tabs">
                    {tabsConfig.map(tab => (
                        <li className="nav-item" key={tab.id}>
                            <a
                                className={`nav-link${activeTab === tab.id ? " active" : ""}`}
                                style={{ cursor: "pointer" }}
                                onClick={() => setActiveTab(tab.id)}
                            >
                                {tab.label}
                            </a>
                        </li>
                    ))}
                </ul>

                {tabsConfig.map(tab => (
                    <div key={tab.id} className={activeTab === tab.id ? "d-block mt-4" : "d-none"}>
                        <div className="d-flex flex-row justify-content-between align-items-center mb-3">
                            <span className="text-muted" style={{ fontSize: "0.9em" }}>
                                {tab.description}
                            </span>
                            <button
                                className="btn btn-primary"
                                onClick={() => saveTabData(tab)}
                                disabled={savingState[tab.id]}
                            >
                                {savingState[tab.id]
                                    ? <i className="fa fa-spinner fa-spin"></i>
                                    : <i className="fa-solid fa-floppy-disk"></i>
                                }
                            </button>
                        </div>

                        <table className="table table-bordered" style={{ tableLayout: "fixed" }}>
                            <colgroup>
                                <col style={{ width: "30%" }}/>
                                <col style={{ width: "70%" }}/>
                            </colgroup>
                            <thead>
                            <tr>
                                <th>Type</th>
                                <th>Origine</th>
                            </tr>
                            </thead>
                            <tbody>
                            {tab.fields.map(field => (
                                <tr key={field.key}>
                                    <td>{field.label}</td>
                                    <td>
                                        {isFetching
                                            ? <i className="fa fa-spinner fa-spin"></i>
                                            : <Select
                                                options={origineOptions}
                                                value={origineOptions.find(opt => opt.value === tabsState[tab.id][field.key]) ?? null}
                                                onChange={(option: OrigineOption) => handleSelect(tab.id, field.key, option.value)}
                                            />
                                        }
                                    </td>
                                </tr>
                            ))}
                            </tbody>
                        </table>
                    </div>
                ))}
            </div>
        </div>
    )
}
